import { httpAuth } from 'config/apiClient'
import endpoints from 'constants/endpoints'
import { IComment, RootState } from 'constants/interfaces'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'

export const AI_USER_ID = 'sto-ai'

export const actionTypes = {
  SET_COMMENTS: '[FEED] SET_COMMENTS',
  SET_COMMENTS_COUNTER: '[FEED] SET_COMMENTS_COUNTER',
  SET_IS_FETCH_REPLIES_COUNTER: '[FEED] SET_IS_FETCH_REPLIES_COUNTER',
  SET_VIEWED_COMMENTS_IDS: '[FEED] SET_VIEWED_COMMENTS_IDS',
  SET_COMMENT_ACTIVITY: '[FEED] SET_COMMENT_ACTIVITY',
  UPDATE_COMMENTS_LIKES: '[FEED] UPDATE_COMMENTS_LIKES',
  SET_IS_FETCHING: '[FEED] SET_IS_FETCHING',
  UPDATE_COMMENTS_VIEWS: '[FEED] UPDATE_COMMENTS_VIEWS',
}
export interface ICommentActivity {
  likes: {
    [key: string]: boolean
  }
  views: {
    [key: string]: boolean
  }
}

export const setIsFetching = (isFetching: boolean) => ({
  type: actionTypes.SET_IS_FETCHING,
  payload: isFetching,
})

export const setCommentActivity = ({ oid, data }: { oid: string; data: ICommentActivity }) => ({
  type: actionTypes.SET_COMMENT_ACTIVITY,
  payload: { oid, data },
})

export const updateCommentsLikes = ({ oid, uids }: { oid: string; uids: string[] }) => ({
  type: actionTypes.UPDATE_COMMENTS_LIKES,
  payload: { oid, uids },
})
export const updateCommentsViews = ({ oid, uids }: { oid: string; uids: string[] }) => ({
  type: actionTypes.UPDATE_COMMENTS_VIEWS,
  payload: { oid, uids },
})

export const setComments = ({
  items,
  hasMore,
  key,
  isReply = false,
}: {
  items: IComment[]
  hasMore?: boolean
  key: string
  isReply?: boolean
}) => ({
  type: actionTypes.SET_COMMENTS,
  payload: { items, hasMore, key, isReply },
})
export const setCommentsCounter = ({ count, key, isReply }: { count: number; key: string; isReply?: boolean }) => ({
  type: actionTypes.SET_COMMENTS_COUNTER,
  payload: { count, key, isReply },
})

export const setIsFetchRepliesCounter = (isFetch: boolean) => ({
  type: actionTypes.SET_IS_FETCH_REPLIES_COUNTER,
  payload: isFetch,
})
export const setViewedCommentsIds = ({ key, ids }: { key: string; ids: string[] }) => ({
  type: actionTypes.SET_VIEWED_COMMENTS_IDS,
  payload: { key, ids },
})
interface IUpdateViews {
  postID: string
  viewedIds: string[]
}
export const updateViews =
  ({ postID, viewedIds }: IUpdateViews): ThunkAction<any, RootState, unknown, AnyAction> =>
  async (_, getConfig) => {
    try {
      const viewedCommentsIds = getConfig().comments.viewedCommentsIds[postID]
      const differenceViewedIds = viewedIds.filter((id) => !viewedCommentsIds?.includes(id))

      if (differenceViewedIds.length === 0) return
      const params = { [postID]: differenceViewedIds }
      await httpAuth.put(endpoints.updateViews, params)
    } catch (e) {
      console.log(e)
    }
  }
export const getCommentActivity =
  (oid: string): ThunkAction<any, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const { data } = await httpAuth.get(endpoints.getActivities(oid))
      dispatch(setCommentActivity({ oid, data }))
    } catch (e) {
      console.log(e)
    }
  }

export const updateLikes =
  (oid: string, uid: string): ThunkAction<any, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { likes } = getState().comments.commentsActivity[oid]
    if (likes[uid]) return
    try {
      dispatch(updateCommentsLikes({ oid, uids: [uid] }))
      const { data } = await httpAuth.put(endpoints.updateLikes, { oid })
      return data
    } catch (e) {
      console.log(e)
    }
  }
export const updateCommentViews =
  (oid: string, uid: string): ThunkAction<any, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const commentsActivity = getState().comments.commentsActivity[oid]
    const views = commentsActivity?.views ?? {}
    if (views[uid]) return
    try {
      dispatch(updateCommentsViews({ oid, uids: [uid] }))
      const { data } = await httpAuth.put(endpoints.updateCommentViews, { oid })
      return data
    } catch (e) {
      console.log(e)
    }
  }

export const deleteCommentsOfMedia =
  (
    oids: string[],
    parent_cids: string[],
    comments_count_keys: string[],
    isResetCounter?: boolean
  ): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { commentsState, commentsCounter } = getState().comments

      comments_count_keys.forEach((key, index) => {
        const oid = oids[index]
        const comments = commentsState[oid]?.items || []
        const count = isResetCounter ? 0 : commentsCounter[key] - comments.length

        dispatch(
          setCommentsCounter({
            count: count,
            key,
          })
        )
      })

      const parentCidsString = parent_cids.join(',')

      const res = await httpAuth.delete(endpoints.deleteCommentsOfImage, {
        data: { parent_cid: parentCidsString },
      })

      return !!res
    } catch (e) {
      console.log(e)
      return false
    }
  }

interface IUpdateComment {
  oid: string
  cid: string
  reaction?: string
  object_created_by: string
  reply_cid?: string
  groups?: string[]
  tags?: string[]
  users?: string[]
  message?: string
}

export const updateComment =
  (payload: IUpdateComment): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { reply_cid, oid, cid, reaction, message } = payload
    const { commentsState, repliesState } = getState().comments
    const { uid } = getState().auth
    const targetState = reply_cid ? repliesState : commentsState
    const targetKey = reply_cid ? reply_cid : oid

    const itemToUpdate = targetState[targetKey]?.items.find((comment) => comment.cid === cid)

    if (itemToUpdate && uid) {
      const currentReactions = itemToUpdate.reactions || {}
      const currentUserReaction = currentReactions[uid]

      if (currentUserReaction === reaction) {
        delete currentReactions[uid]
        delete payload.reaction
      } else if (reaction) {
        currentReactions[uid] = reaction
      }
      if (message) {
        itemToUpdate.message = message
        itemToUpdate.updated_at_ts = Math.floor(Date.now() / 1000)
      }
      if (!reaction) {
        delete payload.reaction
      }

      itemToUpdate.reactions = currentReactions
      if (targetState[targetKey]) {
        targetState[targetKey].items = targetState[targetKey].items.map((item) =>
          item.cid === cid ? itemToUpdate : item
        )
      }
    }
    dispatch(setComments({ items: targetState[targetKey].items, key: targetKey, isReply: !!reply_cid }))

    try {
      const { data } = await httpAuth.put(endpoints.comments, payload)
      return !!data
    } catch (e) {
      console.log(e)
      return false
    }
  }

interface ICreateAICommentParams {
  oid: string
  message: string
  parentCid: string
  rasId: string
  objectType: number
  taskId: string
  resultContextId: string
  resultLevel: string
  keyForSave: string
  recurrenceTs?: string | null
  is_ai?: boolean
}

// Posts a comment on behalf of the AI user (sto-ai) with an optimistic Redux update
export const createAIComment =
  (params: ICreateAICommentParams): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      oid,
      message,
      parentCid,
      rasId,
      objectType,
      taskId,
      resultContextId,
      resultLevel,
      keyForSave,
      recurrenceTs,
      is_ai = false,
    } = params
    const { commentsState } = getState().comments

    const now = Date.now() / 1000
    const tempId = `ai-${Date.now()}`

    const dummyComment: IComment = {
      oid,
      cid: tempId,
      message,
      created_at_ts: now,
      updated_at_ts: now,
      parent_cid: parentCid,
      object_type: objectType,
      created_by: AI_USER_ID,
      result_context_id: resultContextId,
      result_level: resultLevel,
      reply_to: '',
      reply_cid: '',
      isDummy: true,
      is_ai,
    }

    const currentItems = commentsState[keyForSave]?.items ?? []
    dispatch(setComments({ items: [...currentItems, dummyComment], key: keyForSave }))

    const body = {
      oid,
      message,
      object_type: objectType,
      rg_id: rasId,
      parent_cid: parentCid,
      object_created_by: AI_USER_ID,
      task_id: taskId,
      result_context_id: resultContextId,
      result_level: resultLevel,
      is_update_post: false,
      ...(recurrenceTs ? { recurrence_ts: recurrenceTs } : {}),
      ...(is_ai ? { is_ai: true } : {}),
    }

    try {
      const { data } = await httpAuth.post(endpoints.comments, body)
      if (data) {
        dispatch(setComments({ items: [...currentItems, data], key: keyForSave }))
      }
      return true
    } catch (e) {
      console.error('Failed to create AI comment:', e)
      return false
    }
  }
